/**
 * CreditCardExpenses — Accounts sub-page dedicated to Company Credit
 * Card spend (GEDS PEMO, EMS RAK Bank, and any user-added card).
 *
 * Mirrors the structural feel of PettyCash.js but scoped to a single
 * ledger of Paid entries backed by /api/credit-card-expenses.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from './ui/table';
import {
  Plus, CreditCard, Download, FileText, Loader2, Trash2, Edit2,
  Upload, Settings, RefreshCw,
} from 'lucide-react';
import { formatErrorMessage } from '../utils/formatError';
import { CurrencyDisplay } from './DirhamSymbol';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token') || ''}`,
  },
});
const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  id: '',
  card_key: '',
  expense_date: todayISO(),
  amount: '',
  vendor_name: '',
  category: '',
  reference: '',
  remarks: '',
  invoice_url: '',
});

/**
 * Turn a possibly-relative attachment URL into an absolute one so
 * clicking "View" opens a real destination instead of Chrome's
 * ``about:blank#blocked`` intermediate. Relative ``/api/...`` links
 * resolve against the backend URL rather than the frontend origin.
 */
const absolutize = (u) => {
  if (!u) return u;
  if (/^(https?:|data:|blob:)/i.test(u)) return u;
  const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
  return u.startsWith('/') ? `${base}${u}` : `${base}/${u}`;
};

const isAdminUser = (user) => {
  const role = (user?.role || '').toLowerCase();
  return role === 'super admin' || role === 'admin' || role === 'hr admin' || role === 'super_admin';
};

const CreditCardExpenses = ({ user }) => {
  const [cards, setCards] = useState([]);
  const [summary, setSummary] = useState({ cards: [], mtd_total: 0, all_time_total: 0, month: '' });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filterCard, setFilterCard] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [q, setQ] = useState('');

  // Add/Edit dialog
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // Cards management dialog
  const [cardsMgmtOpen, setCardsMgmtOpen] = useState(false);
  const [newCardName, setNewCardName] = useState('');
  const [newCardBank, setNewCardBank] = useState('');
  const [newCardHolder, setNewCardHolder] = useState('');
  const [newCardLast4, setNewCardLast4] = useState('');

  // Delete confirm
  const [deleteId, setDeleteId] = useState('');

  // Invoice viewer state (fetch → blob URL so cross-origin, CSP, and
  // service-worker quirks on production can't block the render).
  const [viewerUrl, setViewerUrl] = useState('');   // remote URL the user clicked
  const [viewerBlob, setViewerBlob] = useState(''); // blob: URL rendered
  const [viewerType, setViewerType] = useState(''); // MIME type from the fetch
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState('');

  const openViewer = async (url) => {
    if (!url) return;
    const abs = absolutize(url);
    setViewerUrl(abs);
    setViewerBlob('');
    setViewerType('');
    setViewerError('');
    setViewerLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
      const r = await fetch(abs, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      setViewerType(blob.type || '');
      setViewerBlob(URL.createObjectURL(blob));
    } catch (e) {
      setViewerError(`Could not load file (${e.message}). Try downloading instead.`);
    } finally {
      setViewerLoading(false);
    }
  };

  const closeViewer = () => {
    if (viewerBlob) URL.revokeObjectURL(viewerBlob);
    setViewerUrl(''); setViewerBlob(''); setViewerType(''); setViewerError('');
  };

  const isAdmin = isAdminUser(user);

  // ---------------------------------------------------------------------
  // Loaders
  // ---------------------------------------------------------------------
  const loadCards = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/credit-cards`, getAuth());
      setCards(r.data.cards || []);
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Failed to load cards'));
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/credit-card-expenses/summary`, getAuth());
      setSummary(r.data || { cards: [], mtd_total: 0, all_time_total: 0 });
    } catch (_e) { /* silent */ }
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (filterCard && filterCard !== 'all') params.card_key = filterCard;
      if (filterSource && filterSource !== 'all') params.source = filterSource;
      if (q) params.q = q;
      // 90-second timeout: on very slow production connections this
      // gives the server enough time to stream the response before the
      // browser marks it as "aborted".
      const r = await axios.get(`${API}/credit-card-expenses`, {
        ...getAuth(), params, timeout: 90_000,
      });
      setEntries(r.data.entries || []);
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Failed to load expenses'));
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, filterCard, filterSource, q]);

  const loadConstants = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/credit-card-expenses/meta/constants`, getAuth());
      setCategories(r.data.categories || []);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadCards();
    loadConstants();
  }, [loadCards, loadConstants]);

  useEffect(() => {
    loadSummary();
    loadEntries();
  }, [loadSummary, loadEntries]);

  const applyFilters = () => {
    loadEntries();
  };

  const clearFilters = () => {
    setFromDate(''); setToDate(''); setFilterCard('all'); setFilterSource('all'); setQ('');
    setTimeout(loadEntries, 0);
  };

  // ---------------------------------------------------------------------
  // Add / Edit
  // ---------------------------------------------------------------------
  const openAdd = () => {
    const defaultCard = cards.find(c => c.is_active !== false)?.card_key || '';
    setForm({ ...emptyForm(), card_key: defaultCard });
    setFormOpen(true);
  };

  const openEdit = (e) => {
    setForm({
      id: e.id,
      card_key: e.card_key || '',
      expense_date: e.expense_date || todayISO(),
      amount: e.amount || '',
      vendor_name: e.vendor_name || '',
      category: e.category || '',
      reference: e.reference || '',
      remarks: e.remarks || '',
      invoice_url: e.invoice_url || '',
    });
    setFormOpen(true);
  };

  const handleUploadInvoice = async (file) => {
    if (!file) return;
    setUploadingInvoice(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/credit-card-expenses/upload`, fd, {
        ...getAuth(),
        headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' },
      });
      setForm(prev => ({ ...prev, invoice_url: r.data.file_url }));
      toast.success('Invoice uploaded');
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Upload failed'));
    } finally {
      setUploadingInvoice(false);
    }
  };

  const submitForm = async () => {
    if (!form.card_key) { toast.error('Please select a card'); return; }
    if (!form.expense_date) { toast.error('Expense date is required'); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Amount must be greater than 0'); return; }
    setSaving(true);
    try {
      const payload = {
        card_key: form.card_key,
        expense_date: form.expense_date,
        amount: amt,
        vendor_name: form.vendor_name,
        category: form.category,
        reference: form.reference,
        remarks: form.remarks,
        invoice_url: form.invoice_url,
      };
      if (form.id) {
        await axios.put(`${API}/credit-card-expenses/${form.id}`, payload, getAuth());
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/credit-card-expenses`, payload, getAuth());
        toast.success('Expense added');
      }
      setFormOpen(false);
      setForm(emptyForm());
      loadEntries();
      loadSummary();
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Failed to save expense'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/credit-card-expenses/${deleteId}`, getAuth());
      toast.success('Expense deleted');
      setDeleteId('');
      loadEntries();
      loadSummary();
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Delete failed'));
    }
  };

  // ---------------------------------------------------------------------
  // Cards management
  // ---------------------------------------------------------------------
  const addCard = async () => {
    if (!newCardName.trim()) { toast.error('Card name required'); return; }
    try {
      await axios.post(`${API}/credit-cards`, {
        card_name: newCardName.trim(),
        bank: newCardBank.trim(),
        card_holder: newCardHolder.trim(),
        card_number_last4: newCardLast4.trim(),
      }, getAuth());
      toast.success('Card added');
      setNewCardName(''); setNewCardBank(''); setNewCardHolder(''); setNewCardLast4('');
      loadCards();
      loadSummary();
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Failed to add card'));
    }
  };

  const toggleCardActive = async (card) => {
    try {
      await axios.put(`${API}/credit-cards/${card.id}`, {
        card_name: card.card_name,
        bank: card.bank || '',
        card_holder: card.card_holder || '',
        card_number_last4: card.card_number_last4 || '',
        is_active: !card.is_active,
      }, getAuth());
      loadCards();
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Update failed'));
    }
  };

  const runMigration = async () => {
    try {
      const r = await axios.post(`${API}/credit-card-expenses/migrate-from-petty-cash`, {}, getAuth());
      toast.success(`Petty Cash sync — ${r.data.migrated} new, ${r.data.skipped_already_migrated} already migrated`);
      loadEntries();
      loadSummary();
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Migration failed'));
    }
  };

  const runTapMigration = async () => {
    try {
      const r = await axios.post(`${API}/credit-card-expenses/migrate-from-tap`, {}, getAuth());
      toast.success(`TAP sync — ${r.data.migrated} new, ${r.data.skipped_already_migrated} already migrated`);
      loadEntries();
      loadSummary();
    } catch (e) {
      toast.error(formatErrorMessage(e, 'TAP migration failed'));
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    if (filterCard && filterCard !== 'all') params.set('card_key', filterCard);
    if (filterSource && filterSource !== 'all') params.set('source', filterSource);
    if (q) params.set('q', q);
    const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
    // Use fetch to attach auth header, then download the blob
    fetch(`${API}/credit-card-expenses/export.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `credit_card_expenses_${todayISO()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('CSV export failed'));
  };

  const totalOnScreen = useMemo(() =>
    entries.reduce((s, e) => s + Number(e.amount || 0), 0), [entries]
  );

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  const summaryCardColors = [
    { bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-900', muted: 'text-indigo-700' },
    { bg: 'bg-violet-50', border: 'border-violet-300', text: 'text-violet-900', muted: 'text-violet-700' },
    { bg: 'bg-sky-50',    border: 'border-sky-300',    text: 'text-sky-900',    muted: 'text-sky-700' },
    { bg: 'bg-teal-50',   border: 'border-teal-300',   text: 'text-teal-900',   muted: 'text-teal-700' },
    { bg: 'bg-amber-50',  border: 'border-amber-300',  text: 'text-amber-900',  muted: 'text-amber-700' },
  ];

  return (
    <div className="space-y-4">
      {/* Summary tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="bg-slate-800 text-white" data-testid="cc-alltime-total">
          <CardContent className="p-4">
            <div className="text-xs opacity-80 flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Total Spend till date
            </div>
            <div className="text-2xl font-bold mt-1"><CurrencyDisplay amount={summary.all_time_total || 0} /></div>
            <div className="text-[11px] opacity-70 mt-1">All-time · across all cards</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 text-white" data-testid="cc-mtd-total">
          <CardContent className="p-4">
            <div className="text-xs opacity-80 flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> MTD Spend ({summary.month || todayISO().slice(0, 7)})
            </div>
            <div className="text-2xl font-bold mt-1"><CurrencyDisplay amount={summary.mtd_total || 0} /></div>
            <div className="text-[11px] opacity-70 mt-1">Across all cards this month</div>
          </CardContent>
        </Card>
        {summary.cards.map((c, i) => {
          const clr = summaryCardColors[i % summaryCardColors.length];
          return (
            <Card key={c.card_id} className={`${clr.bg} ${clr.border} border`} data-testid={`cc-tile-${c.card_key}`}>
              <CardContent className="p-4">
                <div className={`text-xs ${clr.text} flex items-center justify-between`}>
                  <span>{c.card_name}</span>
                  {!c.is_active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                </div>
                <div className={`text-xl font-bold ${clr.text} mt-1`}>
                  <CurrencyDisplay amount={c.mtd_amount || 0} />
                </div>
                <div className={`text-[11px] ${clr.muted} mt-1`}>
                  MTD · {c.mtd_count} txn · All-time <CurrencyDisplay amount={c.all_time_amount || 0} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters + Actions */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> Credit Card Ledger
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={openAdd} data-testid="cc-add-expense-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Expense
              </Button>
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="cc-export-csv-btn">
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
              {isAdmin && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setCardsMgmtOpen(true)} data-testid="cc-manage-cards-btn">
                    <Settings className="w-4 h-4 mr-1" /> Manage Cards
                  </Button>
                  <Button size="sm" variant="outline" onClick={runMigration} data-testid="cc-migrate-btn" title="Import Company-Credit-Card settlements from Petty Cash">
                    <RefreshCw className="w-4 h-4 mr-1" /> Sync from Petty Cash
                  </Button>
                  <Button size="sm" variant="outline" onClick={runTapMigration} data-testid="cc-migrate-tap-btn" title="Import TAP expenses paid via Company Credit Card">
                    <RefreshCw className="w-4 h-4 mr-1" /> Sync from TAP
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 items-end mb-3">
            <div>
              <Label className="text-xs">From date</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 w-40" data-testid="cc-filter-from" />
            </div>
            <div>
              <Label className="text-xs">To date</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 w-40" data-testid="cc-filter-to" />
            </div>
            <div>
              <Label className="text-xs">Card</Label>
              <Select value={filterCard} onValueChange={setFilterCard}>
                <SelectTrigger className="h-9 w-48" data-testid="cc-filter-card"><SelectValue placeholder="All cards" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cards</SelectItem>
                  {cards.map(c => (
                    <SelectItem key={c.id} value={c.card_key}>{c.card_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger className="h-9 w-48" data-testid="cc-filter-source"><SelectValue placeholder="All sources" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="direct">Direct expense</SelectItem>
                  <SelectItem value="petty_cash_reimbursement">From Petty Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Search</Label>
              <Input placeholder="Vendor, remarks, reference, beneficiary…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" data-testid="cc-filter-q" />
            </div>
            <Button size="sm" onClick={applyFilters} data-testid="cc-apply-filters">Apply</Button>
            <Button size="sm" variant="outline" onClick={clearFilters} data-testid="cc-clear-filters">Clear</Button>
          </div>

          {/* Ledger table */}
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Entry #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Card</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Vendor / Beneficiary</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead className="w-24 text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-6 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</TableCell></TableRow>
                ) : entries.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-6 text-slate-500">No expenses recorded yet</TableCell></TableRow>
                ) : entries.map(e => (
                  <TableRow key={e.id} data-testid={`cc-row-${e.id}`}>
                    <TableCell className="font-mono text-xs">{e.entry_no}</TableCell>
                    <TableCell>{e.expense_date}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="text-xs whitespace-normal break-words max-w-[140px] inline-block leading-tight py-1"
                      >
                        {e.card_name}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-700 text-sm">{e.category || '—'}</TableCell>
                    <TableCell className="text-slate-800">
                      {e.beneficiary_name || e.vendor_name || <span className="text-slate-400">—</span>}
                      {e.beneficiary_unique_id && (
                        <div className="text-[11px] text-slate-500">{e.beneficiary_unique_id}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-rose-700">
                      <CurrencyDisplay amount={e.amount || 0} />
                    </TableCell>
                    <TableCell>
                      {e.source === 'petty_cash_reimbursement' ? (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">Petty Cash</Badge>
                      ) : e.source === 'tap_expense' ? (
                        <Badge className="bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300 text-[10px]">TAP</Badge>
                      ) : (
                        <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300 text-[10px]">Direct</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {(e.has_invoice || e.invoice_url) ? (
                        <button
                          type="button"
                          onClick={async () => {
                            // Lazy-fetch full doc when the list stripped
                            // the base64 invoice_url payload.
                            if (e.invoice_needs_fetch || !e.invoice_url) {
                              try {
                                const r = await axios.get(
                                  `${API}/credit-card-expenses/${e.id}`,
                                  { ...getAuth(), timeout: 30_000 }
                                );
                                const url = r.data?.invoice_url;
                                if (url) return openViewer(url);
                                toast.error('Invoice not available');
                                return;
                              } catch (err) {
                                toast.error(formatErrorMessage(err, 'Failed to load invoice'));
                                return;
                              }
                            }
                            openViewer(e.invoice_url);
                          }}
                          className="text-blue-600 text-xs hover:underline inline-flex items-center gap-1"
                          data-testid={`cc-view-invoice-${e.id}`}
                        >
                          <FileText className="w-3 h-3" /> View
                        </button>
                      ) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {e.created_by_name || e.created_by_email || <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`cc-edit-${e.id}`}>
                          <Edit2 className="w-4 h-4 text-slate-600" />
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="icon" onClick={() => setDeleteId(e.id)} data-testid={`cc-delete-${e.id}`}>
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {entries.length > 0 && (
              <div className="p-2 bg-slate-50 border-t text-right text-sm">
                Showing {entries.length} entries · Total <span className="font-semibold text-rose-700"><CurrencyDisplay amount={totalOnScreen} /></span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit Credit Card Expense' : 'Add Credit Card Expense'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Card *</Label>
              <Select value={form.card_key} onValueChange={(v) => setForm({ ...form, card_key: v })}>
                <SelectTrigger className="h-9" data-testid="cc-form-card"><SelectValue placeholder="Select card" /></SelectTrigger>
                <SelectContent>
                  {cards.filter(c => c.is_active !== false || c.card_key === form.card_key).map(c => (
                    <SelectItem key={c.id} value={c.card_key}>
                      {c.card_name}{c.bank ? ` · ${c.bank}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="h-9" data-testid="cc-form-date" />
            </div>
            <div>
              <Label className="text-xs">Amount (AED) *</Label>
              <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9" data-testid="cc-form-amount" />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-9" data-testid="cc-form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Vendor / Beneficiary</Label>
              <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} className="h-9" placeholder="Company name / person paid" data-testid="cc-form-vendor" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Reference / Txn no.</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="h-9" placeholder="e.g. PEMO-XXXXXX or receipt no." data-testid="cc-form-reference" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Remarks</Label>
              <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} data-testid="cc-form-remarks" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Invoice / Receipt (PDF or image)</Label>
              <div className="flex gap-2 items-center">
                <Input type="file" accept=".pdf,image/*" onChange={(e) => handleUploadInvoice(e.target.files?.[0])} className="h-9" data-testid="cc-form-invoice" />
                {uploadingInvoice && <Loader2 className="w-4 h-4 animate-spin" />}
                {form.invoice_url && !uploadingInvoice && (
                  <button
                    type="button"
                    onClick={() => openViewer(form.invoice_url)}
                    className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    <FileText className="w-3 h-3" /> View uploaded
                  </button>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={submitForm} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="cc-form-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {form.id ? 'Save Changes' : 'Add Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cards management dialog */}
      <Dialog open={cardsMgmtOpen} onOpenChange={setCardsMgmtOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Company Cards</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Card Name</TableHead>
                    <TableHead>Bank</TableHead>
                    <TableHead>Holder</TableHead>
                    <TableHead>Last 4</TableHead>
                    <TableHead className="w-24">Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cards.map(c => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">
                        {c.card_name}
                        {c.is_default && <Badge variant="outline" className="ml-2 text-[10px]">Default</Badge>}
                      </TableCell>
                      <TableCell>{c.bank || '—'}</TableCell>
                      <TableCell className="text-xs">{c.card_holder || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{c.card_number_last4 || '—'}</TableCell>
                      <TableCell>
                        <Button size="sm" variant={c.is_active ? 'default' : 'outline'} onClick={() => toggleCardActive(c)}>
                          {c.is_active ? 'Yes' : 'No'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="border rounded-md p-3 bg-slate-50">
              <div className="text-sm font-medium mb-2">Add a new card</div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Card name (e.g. RAK Titanium)" value={newCardName} onChange={(e) => setNewCardName(e.target.value)} className="h-9" data-testid="cc-new-card-name" />
                <Input placeholder="Bank (optional)" value={newCardBank} onChange={(e) => setNewCardBank(e.target.value)} className="h-9" />
                <Input placeholder="Holder (company)" value={newCardHolder} onChange={(e) => setNewCardHolder(e.target.value)} className="h-9" />
                <Input placeholder="Last 4 digits" value={newCardLast4} onChange={(e) => setNewCardLast4(e.target.value)} className="h-9" maxLength={4} />
              </div>
              <div className="mt-2 text-right">
                <Button size="sm" onClick={addCard} data-testid="cc-new-card-save">
                  <Plus className="w-4 h-4 mr-1" /> Add Card
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCardsMgmtOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId('')}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Delete expense?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600">This will remove the expense from the ledger. This cannot be undone.</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId('')}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={confirmDelete} data-testid="cc-confirm-delete">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice viewer — renders the attachment inline. Uses a blob URL
          fetched with auth so cross-origin/CSP/service-worker issues on
          the production domain can't block the render. */}
      <Dialog open={!!viewerUrl} onOpenChange={(v) => !v && closeViewer()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4" /> Invoice / Receipt
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-slate-100 rounded p-2 min-h-[60vh] flex items-center justify-center">
            {viewerLoading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
            {!viewerLoading && viewerError && <div className="text-sm text-rose-700 px-4 text-center">{viewerError}</div>}
            {!viewerLoading && !viewerError && viewerBlob && (
              viewerType.startsWith('image/') ? (
                <img src={viewerBlob} alt="invoice" className="max-w-full max-h-[75vh] object-contain" />
              ) : (
                <iframe title="invoice" src={viewerBlob} className="w-full h-[75vh] bg-white" />
              )
            )}
          </div>
          <DialogFooter className="pt-2">
            <a
              href={viewerBlob || viewerUrl}
              download
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 mr-auto"
            >
              <Download className="w-3 h-3" /> Download
            </a>
            <Button variant="outline" onClick={closeViewer}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditCardExpenses;
